import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft, Check, Star, Users, Zap, Target, Award, ArrowRight, Sparkles, Heart, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@/components/NotificationSystem';

interface OnboardingStep {
  id: string;
  title: string;
  subtitle: string;
  component: React.ComponentType<OnboardingStepProps>;
  isCompleted?: boolean;
}

interface OnboardingStepProps {
  onNext: () => void;
  onPrevious: () => void;
  onSkip?: () => void;
  stepData?: any;
  updateStepData: (data: any) => void;
}

interface UserProfile {
  name: string;
  expertise: string[];
  experience: string;
  goals: string[];
  bio: string;
  serviceTypes: string[];
}

// Welcome Step
const WelcomeStep: React.FC<OnboardingStepProps> = ({ onNext }) => {
  return (
    <div className="text-center space-y-6">
      <div className="space-y-4">
        <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-beige-400 to-beige-600 flex items-center justify-center">
          <Sparkles className="h-10 w-10 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#837069' }}>
            Welkom bij WOM Plaza!
          </h1>
          <p className="text-lg" style={{ color: '#b6a092' }}>
            De marketplace voor vrouwen met een missie
          </p>
        </div>
      </div>

      <div className="bg-gradient-to-r from-beige-50 to-beige-100 p-6 rounded-xl">
        <h2 className="text-xl font-semibold mb-4" style={{ color: '#837069' }}>
          Wat maakt WOM Plaza bijzonder?
        </h2>
        <div className="grid gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-beige-500 flex items-center justify-center shrink-0">
              <Heart className="h-4 w-4 text-white" />
            </div>
            <p className="text-sm" style={{ color: '#837069' }}>
              Een community van ambitieuze vrouwen die elkaar ondersteunen
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-beige-500 flex items-center justify-center shrink-0">
              <Zap className="h-4 w-4 text-white" />
            </div>
            <p className="text-sm" style={{ color: '#837069' }}>
              Verdien geld met je unieke vaardigheden en expertise
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-beige-500 flex items-center justify-center shrink-0">
              <Target className="h-4 w-4 text-white" />
            </div>
            <p className="text-sm" style={{ color: '#837069' }}>
              Bereik je doelen met ondersteuning van gelijkgestemde vrouwen
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <p className="text-sm" style={{ color: '#b6a092' }}>
          Laten we je profiel instellen zodat andere women on a mission je kunnen vinden! 
          Dit duurt slechts 3 minuten.
        </p>
        <Button
          onClick={onNext}
          size="lg"
          className="w-full"
          style={{ backgroundColor: '#b6a092' }}
        >
          Laten we beginnen! <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

// Profile Setup Step
const ProfileSetupStep: React.FC<OnboardingStepProps> = ({ onNext, onPrevious, stepData, updateStepData }) => {
  const [profile, setProfile] = useState<Partial<UserProfile>>(stepData?.profile || {
    name: '',
    experience: '',
    bio: ''
  });

  const handleNext = () => {
    if (!profile.name?.trim()) return;
    updateStepData({ profile });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#837069' }}>
          Vertel ons over jezelf
        </h2>
        <p className="text-sm" style={{ color: '#b6a092' }}>
          Help andere women on a mission je te leren kennen
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-2 block" style={{ color: '#837069' }}>
            Wat is je naam? *
          </label>
          <Input
            placeholder="Bijv. Sarah van Dijk"
            value={profile.name || ''}
            onChange={(e) => setProfile({ ...profile, name: e.target.value })}
            className="text-base"
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block" style={{ color: '#837069' }}>
            Hoeveel jaar ervaring heb je in je vakgebied?
          </label>
          <select
            value={profile.experience || ''}
            onChange={(e) => setProfile({ ...profile, experience: e.target.value })}
            className="w-full px-3 py-2 border rounded-md text-base"
            style={{ borderColor: '#efe7dd', color: '#837069' }}
          >
            <option value="">Selecteer je ervaring</option>
            <option value="0-1">Starter (0-1 jaar)</option>
            <option value="1-3">Beginner (1-3 jaar)</option>
            <option value="3-5">Ervaren (3-5 jaar)</option>
            <option value="5-10">Expert (5-10 jaar)</option>
            <option value="10+">Veteraan (10+ jaar)</option>
          </select>
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block" style={{ color: '#837069' }}>
            Beschrijf jezelf kort (optioneel)
          </label>
          <Textarea
            placeholder="Bijv. Ik ben een gepassioneerde marketeer die ondernemers helpt hun dromen waar te maken..."
            value={profile.bio || ''}
            onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
            rows={3}
            maxLength={200}
          />
          <div className="text-xs text-right mt-1" style={{ color: '#b6a092' }}>
            {profile.bio?.length || 0}/200
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={onPrevious}
          className="flex-1 hover:bg-beige-100"
          style={{ borderColor: '#efe7dd' }}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Terug
        </Button>
        <Button
          onClick={handleNext}
          disabled={!profile.name?.trim()}
          className="flex-1"
          style={{ backgroundColor: '#b6a092' }}
        >
          Volgende
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

// Expertise Step
const ExpertiseStep: React.FC<OnboardingStepProps> = ({ onNext, onPrevious, stepData, updateStepData }) => {
  const [selectedExpertise, setSelectedExpertise] = useState<string[]>(stepData?.expertise || []);

  const expertiseOptions = [
    { id: 'marketing', label: 'Marketing & Sales' },
    { id: 'design', label: 'Design & Creativiteit' },
    { id: 'writing', label: 'Schrijven & Content' },
    { id: 'photography', label: 'Fotografie' },
    { id: 'coaching', label: 'Coaching & Training' },
    { id: 'tech', label: 'Tech & Development' },
    { id: 'business', label: 'Business Consulting' },
    { id: 'admin', label: 'Administratie & Support' },
    { id: 'wellness', label: 'Wellness & Lifestyle' },
    { id: 'education', label: 'Onderwijs & Training' },
    { id: 'finance', label: 'Financiën & Boekhouding' },
    { id: 'legal', label: 'Juridisch Advies' }
  ];

  const toggleExpertise = (expertiseId: string) => {
    setSelectedExpertise(prev => 
      prev.includes(expertiseId)
        ? prev.filter(id => id !== expertiseId)
        : [...prev, expertiseId]
    );
  };

  const handleNext = () => {
    updateStepData({ expertise: selectedExpertise });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#837069' }}>
          Wat zijn je expertises?
        </h2>
        <p className="text-sm" style={{ color: '#b6a092' }}>
          Selecteer alle gebieden waarin je ervaring hebt (meerdere opties mogelijk)
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {expertiseOptions.map((option) => (
          <button
            key={option.id}
            onClick={() => toggleExpertise(option.id)}
            className={`p-4 rounded-lg border text-left transition-all hover:shadow-sm ${
              selectedExpertise.includes(option.id)
                ? 'border-beige-400 bg-beige-100 shadow-sm'
                : 'border-beige-200 hover:bg-beige-50'
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">{option.icon}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate" style={{ color: '#837069' }}>
                  {option.label}
                </div>
              </div>
              {selectedExpertise.includes(option.id) && (
                <Check className="h-4 w-4 text-beige-600" />
              )}
            </div>
          </button>
        ))}
      </div>

      {selectedExpertise.length > 0 && (
        <div className="p-4 rounded-lg" style={{ backgroundColor: '#fbf8f4' }}>
          <p className="text-sm font-medium mb-2" style={{ color: '#837069' }}>
            Je hebt {selectedExpertise.length} expertise{selectedExpertise.length !== 1 ? 's' : ''} geselecteerd:
          </p>
          <div className="flex flex-wrap gap-1">
            {selectedExpertise.map(id => {
              const option = expertiseOptions.find(opt => opt.id === id);
              return (
                <Badge key={id} variant="secondary" className="text-xs">
                  {option?.label}
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={onPrevious}
          className="flex-1 hover:bg-beige-100"
          style={{ borderColor: '#efe7dd' }}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Terug
        </Button>
        <Button
          onClick={handleNext}
          disabled={selectedExpertise.length === 0}
          className="flex-1"
          style={{ backgroundColor: '#b6a092' }}
        >
          Volgende
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

// Goals Step
const GoalsStep: React.FC<OnboardingStepProps> = ({ onNext, onPrevious, stepData, updateStepData }) => {
  const [selectedGoals, setSelectedGoals] = useState<string[]>(stepData?.goals || []);

  const goalOptions = [
    { id: 'income', label: 'Extra inkomen verdienen', description: 'Verdien bij met je vaardigheden' },
    { id: 'business', label: 'Mijn eigen business starten', description: 'Van bijverdienste naar full-time ondernemerschap' },
    { id: 'network', label: 'Netwerk uitbreiden', description: 'Ontmoet gelijkgestemde vrouwen' },
    { id: 'skills', label: 'Nieuwe vaardigheden leren', description: 'Groei door samenwerking' },
    { id: 'flexibility', label: 'Flexibel werken', description: 'Werk wanneer het jou uitkomt' },
    { id: 'impact', label: 'Maatschappelijke impact maken', description: 'Help andere vrouwen succesvol te worden' }
  ];

  const toggleGoal = (goalId: string) => {
    setSelectedGoals(prev => 
      prev.includes(goalId)
        ? prev.filter(id => id !== goalId)
        : [...prev, goalId]
    );
  };

  const handleNext = () => {
    updateStepData({ goals: selectedGoals });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#837069' }}>
          Wat zijn je doelen?
        </h2>
        <p className="text-sm" style={{ color: '#b6a092' }}>
          Selecteer wat je wilt bereiken op WOM Plaza
        </p>
      </div>

      <div className="space-y-3">
        {goalOptions.map((option) => (
          <button
            key={option.id}
            onClick={() => toggleGoal(option.id)}
            className={`w-full p-4 rounded-lg border text-left transition-all hover:shadow-sm ${
              selectedGoals.includes(option.id)
                ? 'border-beige-400 bg-beige-100 shadow-sm'
                : 'border-beige-200 hover:bg-beige-50'
            }`}
          >
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-beige-500 flex items-center justify-center shrink-0 mt-1">
                <Target className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium mb-1" style={{ color: '#837069' }}>
                  {option.label}
                </div>
                <div className="text-sm" style={{ color: '#b6a092' }}>
                  {option.description}
                </div>
              </div>
              {selectedGoals.includes(option.id) && (
                <Check className="h-5 w-5 text-beige-600 shrink-0 mt-1" />
              )}
            </div>
          </button>
        ))}
      </div>

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={onPrevious}
          className="flex-1 hover:bg-beige-100"
          style={{ borderColor: '#efe7dd' }}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Terug
        </Button>
        <Button
          onClick={handleNext}
          disabled={selectedGoals.length === 0}
          className="flex-1"
          style={{ backgroundColor: '#b6a092' }}
        >
          Volgende
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

// Complete Step
const CompleteStep: React.FC<OnboardingStepProps> = ({ stepData }) => {
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const [isCompleting, setIsCompleting] = useState(false);

  const handleComplete = async () => {
    setIsCompleting(true);
    
    // Mock API call to save profile
    setTimeout(() => {
      addNotification({
        type: 'success',
        title: 'Welkom bij WOM Plaza!',
        message: 'Je profiel is succesvol aangemaakt. Je bent nu klaar om te starten!'
      });
      
      // Navigate to marketplace
      navigate('/marketplace');
    }, 2000);
  };

  const handleGoToProfile = () => {
    navigate('/add-service');
  };

  return (
    <div className="text-center space-y-6">
      <div className="space-y-4">
        <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center">
          <Check className="h-10 w-10 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#837069' }}>
            Welkom in de WOM Plaza community!
          </h1>
          <p className="text-lg" style={{ color: '#b6a092' }}>
            Je bent nu klaar om je mission te starten
          </p>
        </div>
      </div>

      {/* Profile Summary */}
      <Card className="border-0 shadow-sm text-left">
        <CardHeader className="pb-3">
          <h3 className="font-semibold" style={{ color: '#837069' }}>
            Jouw Profiel Samenvatting
          </h3>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <span className="text-sm font-medium" style={{ color: '#837069' }}>Naam: </span>
            <span className="text-sm" style={{ color: '#b6a092' }}>{stepData?.profile?.name}</span>
          </div>
          <div>
            <span className="text-sm font-medium" style={{ color: '#837069' }}>Ervaring: </span>
            <span className="text-sm" style={{ color: '#b6a092' }}>{stepData?.profile?.experience}</span>
          </div>
          <div>
            <span className="text-sm font-medium" style={{ color: '#837069' }}>Expertises: </span>
            <div className="flex flex-wrap gap-1 mt-1">
              {stepData?.expertise?.slice(0, 3).map((exp: string) => (
                <Badge key={exp} variant="secondary" className="text-xs">
                  {exp}
                </Badge>
              ))}
              {stepData?.expertise?.length > 3 && (
                <Badge variant="secondary" className="text-xs">
                  +{stepData.expertise.length - 3} meer
                </Badge>
              )}
            </div>
          </div>
          <div>
            <span className="text-sm font-medium" style={{ color: '#837069' }}>Doelen: </span>
            <span className="text-sm" style={{ color: '#b6a092' }}>{stepData?.goals?.length} geselecteerd</span>
          </div>
        </CardContent>
      </Card>

      {/* Next Steps */}
      <div className="bg-gradient-to-r from-beige-50 to-beige-100 p-6 rounded-xl">
        <h3 className="font-semibold mb-4" style={{ color: '#837069' }}>
          Wat kun je nu doen?
        </h3>
        <div className="space-y-3">
          <div className="flex items-center gap-3 text-left">
            <div className="w-8 h-8 rounded-full bg-beige-500 flex items-center justify-center shrink-0">
              <Zap className="h-4 w-4 text-white" />
            </div>
            <p className="text-sm" style={{ color: '#837069' }}>
              Voeg je eerste dienst of product toe
            </p>
          </div>
          <div className="flex items-center gap-3 text-left">
            <div className="w-8 h-8 rounded-full bg-beige-500 flex items-center justify-center shrink-0">
              <Users className="h-4 w-4 text-white" />
            </div>
            <p className="text-sm" style={{ color: '#837069' }}>
              Ontdek wat andere women on a mission aanbieden
            </p>
          </div>
          <div className="flex items-center gap-3 text-left">
            <div className="w-8 h-8 rounded-full bg-beige-500 flex items-center justify-center shrink-0">
              <Award className="h-4 w-4 text-white" />
            </div>
            <p className="text-sm" style={{ color: '#837069' }}>
              Bouw je reputatie op met reviews en ratings
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <Button
          onClick={handleComplete}
          disabled={isCompleting}
          size="lg"
          className="w-full"
          style={{ backgroundColor: '#b6a092' }}
        >
          {isCompleting ? 'Profiel wordt aangemaakt...' : 'Naar WOM Plaza'}
          {!isCompleting && <ArrowRight className="h-4 w-4 ml-2" />}
        </Button>
        
        <Button
          variant="outline"
          onClick={handleGoToProfile}
          className="w-full hover:bg-beige-100"
          style={{ borderColor: '#efe7dd' }}
        >
          Eerste dienst toevoegen
        </Button>
      </div>
    </div>
  );
};

// Main Onboarding Component
const OnboardingFlow: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [stepData, setStepData] = useState<any>({});

  const steps: OnboardingStep[] = [
    {
      id: 'welcome',
      title: 'Welkom',
      subtitle: 'Welkom bij WOM Plaza',
      component: WelcomeStep
    },
    {
      id: 'profile',
      title: 'Profiel',
      subtitle: 'Stel je profiel in',
      component: ProfileSetupStep
    },
    {
      id: 'expertise',
      title: 'Expertise',
      subtitle: 'Kies je vakgebieden',
      component: ExpertiseStep
    },
    {
      id: 'goals',
      title: 'Doelen',
      subtitle: 'Wat wil je bereiken?',
      component: GoalsStep
    },
    {
      id: 'complete',
      title: 'Voltooid',
      subtitle: 'Je bent klaar!',
      component: CompleteStep
    }
  ];

  const progress = ((currentStep + 1) / steps.length) * 100;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const updateStepData = (data: any) => {
    setStepData(prev => ({ ...prev, ...data }));
  };

  if (!isOpen) return null;

  const CurrentStepComponent = steps[currentStep].component;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto border-0 shadow-xl">
        <CardHeader className="sticky top-0 bg-white border-b" style={{ borderColor: '#efe7dd' }}>
          {/* Progress Bar */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm" style={{ color: '#b6a092' }}>
                Stap {currentStep + 1} van {steps.length}
              </div>
              {currentStep > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="text-xs hover:bg-beige-100"
                  style={{ color: '#b6a092' }}
                >
                  Later voltooien
                </Button>
              )}
            </div>
            <Progress value={progress} className="h-2" />
            <div>
              <h2 className="font-semibold" style={{ color: '#837069' }}>
                {steps[currentStep].title}
              </h2>
              <p className="text-sm" style={{ color: '#b6a092' }}>
                {steps[currentStep].subtitle}
              </p>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-6">
          <CurrentStepComponent
            onNext={handleNext}
            onPrevious={handlePrevious}
            stepData={stepData}
            updateStepData={updateStepData}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingFlow;